<script setup lang="ts">
import {
  AtomInput,
  AtomText,
  SatFormControl,
  AtomButton,
  SatMap,
  SatMapInfoWindow,
  SatMapMarker,
  AtomLoadingSpinner,
} from "@chatfood/design-system";
import { bemBuilder } from "@chatfood/core-utils";
import type { IAddressFormEmits } from "./emits";
import { useNewAddress } from "./use-new-address";
import { useGuestAddress } from "./use-guest-address";
import { useExistingAddress } from "./use-existing-address";
import { usePublicConfig } from "~/config";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";

const css = bemBuilder("address-form");

const emits = defineEmits<IAddressFormEmits>();

const business = useBusinessStore();
const basket = useBasketStore();
const customer = useCustomerStore();

const config = usePublicConfig();

const { push } = useNavigation();
const { t } = useLocale();

const isEditFlow = Boolean(basket.addressId);

const { saveAddress, isSaving, formErrors, record, setFieldValue, isLoading } =
  customer.isLogged
    ? isEditFlow
      ? useExistingAddress()
      : useNewAddress()
    : useGuestAddress();

const onSave = async () => {
  await saveAddress(() => {
    emits("created");
  });
};

const onRefineLocation = () => {
  emits("refined");
  push("order", {
    orderingMode: "delivery",
  });
};
</script>

<template>
  <div :class="css('')">
    <AtomLoadingSpinner v-if="isLoading" />
    <AtomText type="h3">
      {{ t("components.address_form.title") }}
    </AtomText>
    <AtomText
      v-if="isEditFlow"
      type="body"
      color="color-error"
      data-test="confirm-warn"
    >
      {{ t("components.address_form.confirm_details_warn") }}
    </AtomText>
    <SatMap
      :class="css('map')"
      :api-key="config.googleMapsApiKey"
      :country="business.country"
      :options="{
        zoomControl: false,
        draggable: false,
        scrollwheel: false,
        clickableIcons: false,
        disableDefaultUI: true,
      }"
      :map-bounds="{
        coordinates: { lat: record.lat, lng: record.lng },
      }"
    >
      <SatMapMarker
        :draggable="false"
        :position="{ lat: record.lat, lng: record.lng }"
        :marker-color="business.designTokens['color-primary']"
      >
        <SatMapInfoWindow :is-open="true">
          <AtomButton
            size="large"
            :class="css('refine-btn')"
            @click.prevent="onRefineLocation"
          >
            {{ t("components.address_form.refine_cta") }}
          </AtomButton>
        </SatMapInfoWindow>
      </SatMapMarker>
    </SatMap>
    <AtomText
      v-if="formErrors.lat"
      tag="div"
      color="color-error"
      data-test="lat-error"
    >
      {{ formErrors.lat }}
    </AtomText>
    <AtomText
      v-if="formErrors.lng"
      tag="div"
      color="color-error"
      data-test="lng-error"
    >
      {{ formErrors.lng }}
    </AtomText>
    <div
      v-if="customer.isLogged"
      :class="css('address-type-block')"
      data-type="address-type"
    >
      <AtomButton
        :wide="true"
        variant="secondary"
        :theme="record.nickname === 'home' ? 'filled' : 'ghost'"
        size="medium"
        data-test="nickname-home"
        @click="setFieldValue('nickname', 'home')"
      >
        {{ t("components.address_form.address_type.home") }}
      </AtomButton>
      <AtomButton
        :wide="true"
        variant="secondary"
        :theme="record.nickname === 'work' ? 'filled' : 'ghost'"
        size="medium"
        data-test="nickname-work"
        @click="setFieldValue('nickname', 'work')"
      >
        {{ t("components.address_form.address_type.work") }}
      </AtomButton>
      <AtomButton
        :wide="true"
        variant="secondary"
        :theme="record.nickname === 'other' ? 'filled' : 'ghost'"
        size="medium"
        data-test="nickname-other"
        @click="setFieldValue('nickname', 'other')"
      >
        {{ t("components.address_form.address_type.other") }}
      </AtomButton>
    </div>
    <AtomText
      v-if="formErrors.nickname"
      tag="div"
      color="color-error"
      data-test="nickname-error"
    >
      {{ formErrors.nickname }}
    </AtomText>
    <form @submit.prevent="onSave">
      <SatFormControl
        :class="css('field-content')"
        :label="t('components.address_form.address_label')"
        :error-message="formErrors.location"
        data-test="location"
      >
        <AtomInput
          autofocus
          :value="record.location"
          :placeholder="t('components.address_form.address_placeholder')"
          :has-error="Boolean(formErrors.location)"
          @keyup="(value) => setFieldValue('location', value)"
        />
      </SatFormControl>
      <SatFormControl
        :class="css('field-content')"
        :label="t('components.address_form.address_number_label')"
        :error-message="formErrors.number"
        data-test="number"
        optional
      >
        <AtomInput
          :value="record.number"
          :placeholder="t('components.address_form.address_number_placeholder')"
          :has-error="Boolean(formErrors.number)"
          @keyup="(value) => setFieldValue('number', value)"
        />
      </SatFormControl>
      <SatFormControl
        :class="css('field-content')"
        :label="t('components.address_form.city_label')"
        :error-message="formErrors.city"
        optional
        data-test="city"
      >
        <AtomInput
          :value="record.city"
          :placeholder="t('components.address_form.city_placeholder')"
          :has-error="Boolean(formErrors.city)"
          @keyup="(value) => setFieldValue('city', value)"
        />
      </SatFormControl>
      <SatFormControl
        :class="css('field-content')"
        :label="t('components.address_form.postcode_label')"
        :error-message="formErrors.zipCode"
        data-test="zipCode"
        :optional="!business.config.isPostalCodeRequired"
      >
        <AtomInput
          :value="record.zipCode"
          :placeholder="t('components.address_form.postcode_placeholder')"
          :has-error="Boolean(formErrors.zipCode)"
          @keyup="(value) => setFieldValue('zipCode', value)"
        />
      </SatFormControl>
      <SatFormControl
        :class="css('field-content')"
        :label="t('components.address_form.delivery_details_label')"
        :error-message="formErrors.instruction"
        data-test="instruction"
        optional
      >
        <AtomInput
          :value="record.instruction"
          :placeholder="
            t('components.address_form.delivery_details_placeholder')
          "
          :has-error="Boolean(formErrors.instruction)"
          @keyup="(value) => setFieldValue('instruction', value)"
        />
      </SatFormControl>
      <AtomButton
        :class="css('save-cta')"
        :wide="true"
        size="large"
        type="submit"
        :is-loading="isSaving"
        data-test="save-address"
        @click="onSave"
      >
        {{ t("components.address_form.save_address") }}
      </AtomButton>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.address-form {
  display: flex;
  flex-direction: column;
  width: 375px;
  max-width: 100%;

  &__map {
    width: 100%;
    height: 200px;
    margin-top: var(--spacing-lg);
    border-radius: var(--radii-card-sm);
    overflow: hidden;
  }

  &__refine-btn {
    width: 210px;
    max-width: 100%;
  }

  &__address-type-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--spacing-md);
    gap: var(--spacing-md);
  }

  &__field-content {
    margin-top: var(--spacing-md);
  }

  &__save-cta {
    margin-top: var(--spacing-lg);
  }
}
</style>
